import { handleResponseOld, handleResponseUseItemApiError, handleResponseWithNon200Errors, makePost } from './helpers';
import { BiddingInfo } from '@/types/BiddingInfo';
import { CatalogRegistration } from '@/types/CatalogRegistration';

/**
 * @category User API
 * @see postChangePassword
 */
type PostChangePasswordParams = {
    authToken: string;
    deployment: string;
    newPassword: string;
    oldPassword: string;
};

type PostChangePasswordResponse = {
    error: boolean;
    payload: boolean;
};

/**
 * postChangePassword - updates user password
 * @category User API
 * @function postChangePassword
 * @param {PostChangePasswordParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/user/updatepassword
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/g3qvanjl6a/resources/p4t7pq/methods/POST
 */
export const postChangePassword = ({ authToken, deployment, newPassword, oldPassword }: PostChangePasswordParams) =>
    new Promise<PostChangePasswordResponse>((resolve, reject) => {
        const request = makePost({ apiPath: 'updatepassword', authToken, deployment, path: '<USER-API>' });
        request.send({ newPassword, oldPassword });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

type AcceptedTermsOfService = 'accepted' | 'unknown' | 'withdrawn';

/**
 * @category Item API
 * @see postConfirmPassword
 */
type PostConfirmPasswordParams = {
    deployment: string;
    password: string;
    username: string;
};

type BidderProfileDataResponse = {
    data: {
        annualNMS: number;
        bidder: {
            acceptedTermsOfService: AcceptedTermsOfService;
            address: string;
            address2: string;
            bidderId: number;
            city: string;
            country: string;
            createdDate: string;
            creditCardValidated: boolean;
            email: string;
            firstName: string;
            hasEverRegisteredForAuction: boolean;
            lastName: string;
            mobileNumber: string;
            phoneNumber: string;
            profileComplete: boolean;
            standing: string;
            state: string;
            username: string;
            zip: string;
            zipCodeGeoCoordinates: {
                latitude: number;
                longitude: number;
            };
        };
        biddingInfos: BiddingInfo[];
        catalogRegistration: CatalogRegistration[];
        followedSellers: number[];
        mainhostLogin: {
            '5Xq_name': string;
            '5Xq_nuid': string;
            '5Xq_nverify': string;
        };
        resellerType: number;
        savedItems: number[];
        savedItemsTime: [
            {
                itemId: string;
                saveTs: number;
            }
        ];
        searchExclusions: {
            houseIds: number[];
        };
        stripeCustomer: object;
        token: string;
    };
    message: string;
    success: boolean;
};

/**
 * postConfirmPassword - login process, confirms password and returns auth token for login
 * @category Item API
 * @function postConfirmPassword
 * @param {PostConfirmPasswordParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/auth/spalogin
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/react-site/routes/post-spa-login.ts
 */
export const postConfirmPassword = ({ deployment, password, username }: PostConfirmPasswordParams) =>
    new Promise<BidderProfileDataResponse>((resolve, reject) => {
        const request = makePost({ deployment, path: '<ITEM-API>auth/spalogin' });
        request.send({ password, username });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category User API
 * @see postForgotPassword
 */
type PostForgotPasswordParams = {
    deployment: string;
    emailAddress: string;
    sendSMS: boolean;
};

type PostForgotPasswordResponse = {
    error: boolean;
    payload: boolean;
};

/**
 * postForgotPassword - sends request for password recovery notification
 * @category User API
 * @function postForgotPassword
 * @param {PostForgotPasswordParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/user/forgotpassword
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/g3qvanjl6a/resources/ywmkti/methods/POST
 */
export const postForgotPassword = ({ deployment, emailAddress, sendSMS }: PostForgotPasswordParams) =>
    new Promise<PostForgotPasswordResponse>((resolve, reject) => {
        const request = makePost({
            apiPath: 'forgotpassword',
            deployment,
            path: '<USER-API>',
        });
        request.send({ sendSMS, username: emailAddress });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see postResetPassword
 */
type PostResetPasswordParams = {
    bidderId: number;
    deployment: string;
    hash: string;
    newPassword: string;
};

/**
 * postResetPassword - sends password reset information
 * @category Item API
 * @function postResetPassword
 * @param {PostResetPasswordParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/auth/reset-password
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/react-site/routes/post-spa-reset-password.ts
 */
export const postResetPassword = ({ bidderId, deployment, hash, newPassword }: PostResetPasswordParams) =>
    new Promise<BidderProfileDataResponse>((resolve, reject) => {
        const request = makePost({ deployment, path: '<ITEM-API>auth/spa/reset-password' });
        request.send({ bidderId, hash, newPassword });
        request.end((err, response) => handleResponseUseItemApiError({ err, reject, resolve, response }));
    });
